/**
 * Le nom sous lequel une personne parait dans l'application : SOURCE UNIQUE.
 *
 * La regle est décidée par le serveur, qui sert un `displayName` : le nom
 * commercial du responsable s'il en a déclaré un, sinon son prénom et son nom.
 * L'application ne la rejoue pas, elle la lit — et retombe sur l'état civil
 * quand une réponse ancienne ou partielle ne porte pas le champ.
 *
 * **Ou l'on ne s'en sert pas.** Une commande, un recu, un litige, un dossier
 * d'identité et l'administration nomment les gens par leur **nom légal** :
 * l'enseigne se change, ce qui est opposable, non. Ces écrans-la appellent
 * `legalName`, et le disent.
 */
export type NamedPerson = {
  displayName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
};

/** Le nom d'affichage. Jamais vide : a défaut de tout, le repere `—`. */
export function personName(person?: NamedPerson | null, fallback = '—'): string {
  if (!person) {
    return fallback;
  }
  const display = person.displayName?.trim();
  if (display) {
    return display;
  }
  return legalName(person, fallback);
}

/** Prénom et nom, pour les documents et les dossiers. */
export function legalName(
  person?: { firstName?: string | null; lastName?: string | null } | null,
  fallback = '—',
): string {
  if (!person) {
    return fallback;
  }
  const legal = `${person.firstName ?? ''} ${person.lastName ?? ''}`.trim();
  return legal === '' ? fallback : legal;
}

/**
 * Le nom légal n'est utile a coté de l'enseigne que s'il en differe : sinon,
 * l'écran répète deux fois la meme chose.
 */
export function legalNameBehind(person?: NamedPerson | null): string | null {
  if (!person?.displayName) {
    return null;
  }
  const legal = legalName(person, '');
  return legal !== '' && legal !== person.displayName.trim() ? legal : null;
}
